import { GRPCChartNullablePoints } from "@/components/charts/GRPCMetricChartImpl";
import { DateRangeValue } from "@/components/interactive/SharedTimeRange.types";
import { audit } from "@/gen_src/proto/chalk/auth/v1/audit_pb";
import { DenseTimeSeriesChart } from "@/gen_src/proto/chalk/chart/v1/densetimeserieschart_pb";
import { ChartSchema, SeriesSchema } from "@/gen_src/proto/chalk/server/v1/chart_pb";
import { FieldBehavior, field_behavior } from "@/gen_src/proto/google/api/field_behavior_pb";
import { isDefined } from "@/utils/function-utils";
import { JsonType, camelCaseToSnakeCase } from "@/utils/string-utils";
import type {
  DescField,
  DescMessage,
  MessageInitShape,
  MessageJsonType,
  MessageShape,
} from "@bufbuild/protobuf";
import {
  DescMethod,
  create as createProto,
  getOption,
  hasOption,
  isMessage as isProtoMessage,
  equals as protoEquals,
  fromBinary as protoFromBinary,
  fromJson as protoFromJson,
  fromJsonString as protoFromJsonString,
  toBinary as protoToBinary,
  toJson as protoToJson,
  toJsonString as protoToJsonString,
} from "@bufbuild/protobuf";
import {
  Duration,
  DurationSchema,
  Timestamp,
  Value,
  ValueSchema,
  timestampDate,
  timestampFromDate,
} from "@bufbuild/protobuf/wkt";
import isEqual from "lodash/isEqual";

export type { MessageInitShape };

// this is just so that this heavily-used function can be imported without clobbering create
export {
  createProto,
  isProtoMessage,
  protoEquals,
  protoFromBinary,
  protoFromJson,
  protoFromJsonString,
  protoToBinary,
  protoToJson,
  protoToJsonString,
};

export const protoIsEmpty = <T extends DescMessage>(
  schema: T,
  message: MessageShape<T>,
): boolean => {
  return protoEquals(schema, message, createProto(schema));
};

export const protoValueToJson = (value: Value): MessageJsonType<typeof ValueSchema> => {
  return protoToJson(ValueSchema, value);
};

export const maybeProtoToJson = <T extends DescMessage>(
  schema: T,
  maybeProto: MessageShape<T> | null | undefined,
): MessageJsonType<T> | null => {
  if (maybeProto == null) {
    return null;
  }

  return protoToJson(schema, maybeProto);
};

export const maybeProtoValueToJson = (
  value: Value | null | undefined,
): MessageJsonType<typeof ValueSchema> | null => {
  return maybeProtoToJson(ValueSchema, value);
};

export const maybeProtoValueToJsonString = (
  value: Value | null | undefined,
): MessageJsonType<typeof ValueSchema> | null => {
  return maybeProtoToJsonString(ValueSchema, value);
};

export const maybeProtoToJsonString = <T extends DescMessage>(
  schema: T,
  maybeProto: MessageShape<T> | null | undefined,
): string | null => {
  if (maybeProto == null) {
    return null;
  }

  return protoToJsonString(schema, maybeProto);
};

export const durationFromSeconds = (seconds: number) => {
  return createProto(DurationSchema, { seconds: BigInt(seconds) });
};

export const durationFromMillis = (millis: number) => {
  const seconds = Math.floor(millis / 1000);
  const nanos = (millis % 1000) * 1e6;
  return createProto(DurationSchema, {
    seconds: BigInt(seconds),
    nanos: Math.trunc(nanos), // empirically can't be a float for whatever reason
  });
};

export const durationToMillis = (duration: Duration): number => {
  return Number(duration.seconds) * 1000 + duration.nanos / 1e6;
};

export const durationToSeconds = (duration: Duration): number => {
  return durationToMillis(duration) / 1000;
};

export const timestampToDate = (value: Timestamp): Date => {
  return timestampDate(value);
};

export const maybeTimestampToDate = (value: Timestamp | null | undefined): Date | null => {
  if (value == null) {
    return null;
  }
  return timestampToDate(value);
};

export const timestampToMillis = (value: Timestamp): number => {
  // return value.toDate().getTime();
  return Number(value.seconds) * 1000 + value.nanos / 1e6;
};

export const safeValueJson = (value: string): Value => {
  try {
    return protoFromJsonString(ValueSchema, value);
  } catch {
    return protoFromJson(ValueSchema, value);
  }
};

export const stringifyProtoValue = (value: Value | null | undefined): string => {
  if (value == null) {
    return "missing";
  }
  switch (value?.kind?.case) {
    case "nullValue":
      return "null";
    case "boolValue":
    case "stringValue":
    case "numberValue":
    case "listValue":
    case "structValue":
    default:
      return `${value.kind.value}`;
  }
};

export const mapTimeSeriesChartToSeriesChart = (
  chart: DenseTimeSeriesChart | null | undefined,
): GRPCChartNullablePoints | null => {
  return chart
    ? {
        ...createProto(ChartSchema, {
          title: chart.title,
          xTimestampMs: chart.xSeries.map((value) => value.seconds * BigInt(1000)),
        }),
        series: chart.series.map((series) => ({
          ...createProto(SeriesSchema, {
            units: series.unit,
            label: series.label,
          }),
          points: series.points.map((point): number | null =>
            isDefined(point.value) ? Number(point.value) : null,
          ),
          groupTags: series.groupTags.map((tag) => {
            const value = String(tag.value?.value.value);
            return {
              group: tag.groupKey,
              value,
              // Snapshots from before value_display existed leave it empty.
              valueDisplay: tag.valueDisplay || value,
            };
          }),
        })),
      }
    : null;
};

export const areTimestampsEqual = (
  tsA: Timestamp | null | undefined,
  tsB: Timestamp | null | undefined,
): boolean => {
  if (!isDefined(tsA) || !isDefined(tsB)) {
    // If one is nullish, fall back to referential equality
    return tsA === tsB;
  }
  return timestampDate(tsA).getTime() === timestampDate(tsB).getTime();
};

export const convertObjectToStringMap = (obj: Record<string, any>): Record<string, string> => {
  return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, value.toString()]));
};

export const convertObjectToProtoMap = (obj: Record<string, any>): Record<string, Value> => {
  return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, safeValueJson(value)]));
};

export const maybeToNumber = (
  value: string | number | bigint | null | undefined,
): number | undefined => {
  if (value == null) {
    return undefined;
  }

  return Number(value);
};

export const rangeToTimestamp = (
  range: DateRangeValue,
): { start: Timestamp | undefined; end: Timestamp | undefined } => {
  return {
    start: range.start ? timestampFromDate(new Date(range.start)) : undefined,
    end: range.end ? timestampFromDate(new Date(range.end)) : undefined,
  };
};

export const safeMessage = <T extends JsonType | bigint>(msg: T): T => {
  if (typeof msg === "bigint") {
    return `${msg}` as T;
  } else if (Array.isArray(msg)) {
    return msg.map(safeMessage) as T;
  } else if (typeof msg === "object" && msg != null) {
    return Object.fromEntries(
      Object.entries(msg).map(([key, value]) => {
        return [key, safeMessage(value)];
      }),
    ) as T;
  }
  return msg;
};

/**
 * Helper to convert bigints to numbers in an object
 */
export function convertBigIntsToNumbers<T>(obj: T): T {
  if (obj == null) return obj;
  if (typeof obj === "bigint") return Number(obj) as T;
  if (Array.isArray(obj)) return obj.map(convertBigIntsToNumbers) as T;
  if (typeof obj === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = convertBigIntsToNumbers(value);
    }
    return result as T;
  }
  return obj;
}

/**
 * Convert a protobuf Duration to a chalk duration string (e.g., "1h", "30m", "7d")
 */
export function durationToChalkString(duration: Duration | null | undefined): string | undefined {
  if (duration == null) return undefined;

  const totalSeconds = Number(duration.seconds);
  if (totalSeconds === 0) return undefined;

  // Convert to the most appropriate unit
  const secondsInMinute = 60;
  const secondsInHour = 60 * 60;
  const secondsInDay = 60 * 60 * 24;

  if (totalSeconds % secondsInDay === 0) {
    return `${totalSeconds / secondsInDay}d`;
  } else if (totalSeconds % secondsInHour === 0) {
    return `${totalSeconds / secondsInHour}h`;
  } else if (totalSeconds % secondsInMinute === 0) {
    return `${totalSeconds / secondsInMinute}m`;
  } else {
    return `${totalSeconds}s`;
  }
}

/**
 * Convert a chalk duration string (e.g., "1h", "30m", "7d") to a protobuf Duration
 */
export function chalkStringToDuration(
  durationStr: string | null | undefined,
): Duration | undefined {
  if (!durationStr) return undefined;

  const timeUnits: Record<string, number> = {
    y: 60 * 60 * 24 * 365, // years
    d: 60 * 60 * 24, // days
    h: 60 * 60, // hours
    m: 60, // minutes
    s: 1, // seconds
  };

  let totalSeconds = 0;
  // Match patterns like "1h", "30m", "7d", "1h30m", etc.
  const regex = /(\d+)([ydhms])/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(durationStr)) !== null) {
    const value = parseInt(match[1], 10);
    const unit = match[2];
    totalSeconds += value * (timeUnits[unit] || 0);
  }

  if (totalSeconds === 0) return undefined;

  return createProto(DurationSchema, { seconds: BigInt(totalSeconds) });
}

export const isAuditedMethod = (method: DescMethod): boolean => {
  return hasOption(method, audit);
};

/**
 * Returns the camelCase field names marked [(google.api.field_behavior) = IMMUTABLE]
 * in the given proto schema. Call once at module level to avoid recomputing.
 */
export function getImmutableFieldNames(schema: { field: Record<string, DescField> }): Set<string> {
  return new Set(
    Object.entries(schema.field)
      .filter(([, fieldDesc]) =>
        getOption(fieldDesc, field_behavior).includes(FieldBehavior.IMMUTABLE),
      )
      .map(([name]) => name),
  );
}

/**
 * Compare two proto-backed spec objects and return the snake_case field paths that changed,
 * skipping fields not in the schema and any fields in the provided immutableFields list.
 * Pass the result of getImmutableFieldNames(schema) as immutableFields.
 */
export function getChangedMutableFieldPaths(
  schema: { field: Record<string, DescField> },
  immutableFields: Set<string>,
  oldSpec: Record<string, unknown>,
  newSpec: Record<string, unknown>,
): string[] {
  const changedPaths: string[] = [];
  for (const [field, descriptor] of Object.entries(schema.field)) {
    if (immutableFields.has(field)) {
      continue;
    }
    // Oneof values live under their group in JS, but masks address the selected proto field.
    const getValue = (spec: Record<string, unknown>): unknown => {
      if (descriptor.oneof == null) return spec[field];
      const selected = spec[descriptor.oneof.localName] as
        | { case?: string; value?: unknown }
        | undefined;
      return selected?.case === field ? selected.value : undefined;
    };
    if (!isEqual(getValue(oldSpec), getValue(newSpec))) {
      changedPaths.push(descriptor.name);
    }
  }

  return changedPaths;
}

/**
 * Compare two proto-backed spec objects and return the snake_case field paths of IMMUTABLE fields
 * that changed. Mirror of getChangedMutableFieldPaths, restricted to immutable fields. Use it to
 * warn the user when they edited fields that cannot be applied: the backend rejects immutable
 * fields in the update mask, and getChangedMutableFieldPaths excludes them, so an edit that only
 * touches immutable fields would otherwise silently produce an empty mask.
 */
export function getChangedImmutableFieldPaths(
  schema: { field: Record<string, DescField> },
  immutableFields: Set<string>,
  oldSpec: Record<string, unknown>,
  newSpec: Record<string, unknown>,
): string[] {
  const changedPaths: string[] = [];
  const allFields = new Set([...Object.keys(oldSpec), ...Object.keys(newSpec)]);

  for (const field of allFields) {
    if (!(field in schema.field) || !immutableFields.has(field)) {
      continue;
    }
    if (!isEqual(oldSpec[field], newSpec[field])) {
      changedPaths.push(camelCaseToSnakeCase(field));
    }
  }

  return changedPaths;
}

// when passing objects to the createProto() function,
// they do not get their defaults if the $typeName field is not there.
// when spreading protos / converting protos to objects that are not meant to be protos, it is
// important to use this function to de-proto-ify. So if/when the object is re-submitted to a proto,
// it will be created with all the proper defaults
export const stripProtoTypeName = <T>(maybeMessage: T): T => {
  if (typeof maybeMessage !== "object" || maybeMessage == null) {
    return maybeMessage;
  }

  if (Array.isArray(maybeMessage)) {
    return maybeMessage.map(stripProtoTypeName) as T;
  }

  return Object.fromEntries(
    Object.entries(maybeMessage)
      .filter(([key]) => key !== "$typeName")
      .map(([key, value]) => [key, stripProtoTypeName(value)]),
  ) as T;
};
